import React, { useState } from 'react'
import { Button as ButtonComponent } from '@/components/ui/button'
import { Input as InputComponent } from '@/components/ui/input'
import { describeMaximumProblem, maximumToMinor } from '@/utils/pixel-warmup'
import { LOWER_ONLY_NOTE, pendingReasonText } from '@/utils/pixel-warmup-panel'
import { useCapPanel } from './hooks/useCapPanel'

const Button = ButtonComponent as React.ComponentType<any>
const Input = InputComponent as React.FC<React.InputHTMLAttributes<HTMLInputElement>>

interface PendingChange {
    ad_set_id: string
    name: string | null
    external_id?: string | null
    reason: string | null
    since: string | null
    /** A reservation of the budget optimiser: nobody checks it by hand. */
    optimiser?: boolean
}

interface CapPanelProps {
    adCampaignId: string
    formatAmount: (minor: number) => string
    /** The entry of this campaign from the batched read. */
    entry: {
        has_maximum: boolean
        max_daily_budget: number | null
        unconfirmed_changes: PendingChange[]
        can_recheck: boolean
        is_owner: boolean
        warmup?: any
    }
    onRefresh: () => Promise<void>
}

const formatWhen = (value?: string | null) => (value ? new Date(value).toLocaleString() : '')

// What must fit under the maximum of a campaign whose warm-up has not finished: the warm-up ad set and the held audiences
const knownMinimum = (warmup: any): number => {
    if (!warmup || ['expanded', 'cancelled'].includes(warmup.state)) return 0
    const held = (warmup.held_rows || []).filter((row: any) => row.held)
    return (
        Number(warmup.warmup_ad_set?.budget || 0) +
        held.reduce((sum: number, row: any) => sum + Number(row.budget || 0), 0)
    )
}

/**
 * The maximum daily budget of a campaign and what is unconfirmed under it. Shown for every campaign that has a maximum,
 * with or without a warm-up, so the owner can see it, raise or lower it, and check a change that Meta has not confirmed
 * again by hand.
 */
const CapPanel: React.FC<CapPanelProps> = ({ adCampaignId, formatAmount, entry, onRefresh }) => {
    // Check again is for the owner; the server says whether the reader is
    const mayRecheck = entry.is_owner === true
    const panel = useCapPanel(adCampaignId, { refresh: onRefresh, formatAmount })
    const [editing, setEditing] = useState(false)
    const [value, setValue] = useState('')
    const [problem, setProblem] = useState<string | null>(null)

    if (!entry.has_maximum) return null

    // With the feature off for the licence a maximum can only be lowered (a global halt does not change that). The warm-up
    // view says so; for a campaign without a warm-up the server's refusal of a raise does
    const loweringOnly =
        (!!entry.warmup && !entry.warmup.feature_enabled) || panel.refusalCode === 'PIXEL_WARMUP_NOT_AVAILABLE'

    const save = async () => {
        const found = describeMaximumProblem(value, { minimumMinor: knownMinimum(entry.warmup) })
        const typedMinor = maximumToMinor(value)
        const notLower =
            !found &&
            loweringOnly &&
            entry.max_daily_budget !== null &&
            typedMinor !== null &&
            typedMinor >= entry.max_daily_budget
        setProblem(
            found || (notLower ? 'Enter an amount below the current maximum: it cannot be raised right now.' : null),
        )
        if (found || notLower) return
        if (await panel.changeMaximum(value)) setEditing(false)
    }

    return (
        <section
            aria-label="Maximum daily budget"
            data-testid="cap-panel"
            className="tw:sticky tw:left-0 tw:w-[1000px] tw:border-b tw:border-[#2B3B4C] tw:bg-[#0B1E33] tw:px-6 tw:py-2 tw:text-sm tw:text-white"
        >
            <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-3">
                <span className="tw:font-semibold">Maximum daily budget</span>
                {editing ? (
                    <span className="tw:flex tw:items-center tw:gap-2">
                        <Input
                            aria-label="New maximum daily budget"
                            className="tw:w-32"
                            inputMode="decimal"
                            value={value}
                            onChange={(event) => setValue(event.target.value)}
                        />
                        <Button size="sm" disabled={panel.busy} onClick={() => void save()}>
                            Save
                        </Button>
                        <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => {
                                setEditing(false)
                                setProblem(null)
                            }}
                        >
                            Cancel
                        </Button>
                    </span>
                ) : (
                    <>
                        <span data-testid="cap-panel-maximum">
                            {entry.max_daily_budget === null
                                ? 'Not shown'
                                : `${formatAmount(entry.max_daily_budget)} a day`}
                        </span>
                        <button
                            type="button"
                            className="tw:text-xs tw:text-blue-400 tw:hover:underline"
                            disabled={panel.busy}
                            onClick={() => {
                                setValue(entry.max_daily_budget === null ? '' : String(entry.max_daily_budget / 100))
                                setEditing(true)
                            }}
                        >
                            Change
                        </button>
                    </>
                )}
                {problem && (
                    <span className="tw:text-yellow-300" role="alert" data-testid="cap-panel-problem">
                        {problem}
                    </span>
                )}
            </div>

            {loweringOnly && (
                <p className="tw:!m-0 tw:mt-1 tw:text-xs tw:text-gray-200" data-testid="cap-panel-lower-only">
                    {LOWER_ONLY_NOTE}
                </p>
            )}

            {entry.unconfirmed_changes.length > 0 && (
                <div className="tw:mt-2" data-testid="cap-panel-unconfirmed">
                    <h3 className="tw:!m-0 tw:!mb-1 tw:!text-sm tw:!font-semibold">Unconfirmed changes</h3>
                    <ul className="tw:!m-0 tw:!p-0 tw:list-none">
                        {entry.unconfirmed_changes.map((change) => (
                            <li key={change.ad_set_id} className="tw:flex tw:flex-wrap tw:gap-3 tw:py-1">
                                <span>
                                    Unconfirmed change: the budget of {change.name || 'an ad set'} is held against your
                                    maximum until Meta confirms it ({pendingReasonText(change.reason)}
                                    {change.since ? `, since ${formatWhen(change.since)}` : ''}).
                                </span>
                                {change.optimiser && (
                                    <span className="tw:text-gray-200" data-testid="cap-panel-optimiser">
                                        Turba settles this change itself.
                                    </span>
                                )}
                                {entry.can_recheck && mayRecheck && !change.optimiser && !!change.external_id && (
                                    <button
                                        type="button"
                                        className="tw:text-blue-400 tw:hover:underline"
                                        disabled={panel.busy}
                                        onClick={() => void panel.recheck(change.ad_set_id)}
                                    >
                                        Check again
                                    </button>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </section>
    )
}

export default CapPanel
